import { useMemo } from "react";

import { useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import {
  GetEvaluationsQuery,
  GetEvaluationsQueryVariables,
} from "@/graphql/generated/graphql";
import { GetEvaluations } from "@/graphql/queries/GetEvaluations";
import { useQueryWrapper } from "@/hooks/useQueryWrapper/useQueryWrapper";

export function useEvaluation(
  callId: GetEvaluationsQueryVariables["filters"]["callId"]
) {
  const { show } = useToast();
  const { t } = useTranslation();

  const { data, loading } = useQueryWrapper<
    GetEvaluationsQuery,
    GetEvaluationsQueryVariables
  >(GetEvaluations, {
    skip: !callId,
    variables: {
      filters: {
        callId,
        limit: 1,
      },
    },
    onCompleted(data) {
      if (data.getEvaluations.__typename !== "PaginatedEvaluations") {
        show({
          variant: "critical",
          message: data.getEvaluations.message,
        });
      }
    },

    onError() {
      show({
        variant: "critical",
        message: t("error.generic.title"),
      });
    },
  });

  const evaluation =
    data?.getEvaluations.__typename === "PaginatedEvaluations"
      ? data.getEvaluations.items[0]
      : null;

  const sortedEvaluation = useMemo(() => {
    if (!evaluation) {
      return null;
    }

    /**
     * We sort categories and questions by `questionOrder` and `categoryOrder`
     */
    const sortedCategoriesAndQuestions = evaluation.categories
      .map((category) => ({
        ...category,
        questions: [...category.questions].sort(
          (a, b) => a.questionOrder - b.questionOrder
        ),
      }))
      .sort((a, b) => a.categoryOrder - b.categoryOrder);

    return {
      ...evaluation,
      categories: sortedCategoriesAndQuestions,
    };
  }, [evaluation]);

  return {
    loading,
    evaluation: sortedEvaluation,
  };
}
